import { Typography } from '@one-thd/sui-atomic-components';
import { useAccountCrumbs } from '@thd-olt-component-react/customer-account-navigation';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import React, { useEffect, useState } from 'react';
import { HelpDialogBox } from '../components/guest-order-tracking/HelpDialogBox';
import { OnlineOrder } from '../components/guest-order-tracking/OnlineOrder';
import { TrackOrderForm } from '../components/guest-order-tracking/TrackOrderForm';
import { Breadcrumbs } from '../components/shared/Breadcrumbs/Breadcrumbs';
import { useCommonContext, useExperienceContext } from '../hooks/useContexts';
import { IGuestOrderTrackingProps } from '../models/guest-order-tracking';
import { IOrderDetailsResponse } from '../models/order-details';
import { getWindow } from '../utils/general';
import { setGuestOrderTrackingPageAnalytics } from '../utils/guest-order-tracking/analytics';
import {
  CommonProvider,
  OrderProvider,
  Phase0BarcodeProvider,
} from '../contexts';
import { EditDeliveryInstructionsProvider } from '../contexts/EditDeliveryInstructionsContext';

export const GuestOrderTrackingContent = ({
  customerType,
}: IGuestOrderTrackingProps) => {
  const { isConsumerApp } = useExperienceContext();
  const { isMobile } = useCommonContext();
  const { isExchangeCustomer, isLoggedIn } = useThdCustomer();

  const urlParams = new URLSearchParams(getWindow()?.location.search);
  const orderId = String(urlParams.get('orderId'));
  const reschedulesId = String(urlParams.get('ecc_ord'));
  const orderNumber = String(urlParams.get('orderNumber'));

  let orderNumberFromUrl = '';
  if (orderId !== 'null') orderNumberFromUrl = orderId;
  if (reschedulesId !== 'null') orderNumberFromUrl = reschedulesId;
  if (orderNumber !== 'null') orderNumberFromUrl = orderNumber;

  const [formData, setFormData] = useState<
    IOrderDetailsResponse | undefined | null
  >(null);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const crumbs = useAccountCrumbs([
    {
      label: 'Track Your Order',
      path: '/start-a-return',
    },
  ]);

  if (formSubmitted) {
    crumbs.splice(
      1,
      2,
      {
        label: 'Track Your Order',
        path: '/myaccount/guest-order-tracking',
      },
      { label: 'Online Order', path: '/guest-order-tracking' },
    );
  } else {
    crumbs?.splice(0, 2, { label: 'Home', path: 'https://www.homedepot.com/' });
  }

  useEffect(() => {
    if (isLoggedIn && !isExchangeCustomer) {
      getWindow()?.location?.replace(
        `https://www.homedepot.com/myaccount/purchase-history`,
      );
    }

    setGuestOrderTrackingPageAnalytics();
  }, []);

  return (
    <div className="sui-grid sui-grid-cols-12 sui-gap-5 sui-py-3 lg:sui-ml-36 lg:sui-mr-36 sui-px-6">
      <div className="no-print sui-col-span-12 sui-gap-2 sui-p-2 sui-w-full">
        {!isConsumerApp && (
          <Breadcrumbs isMobile={isMobile ?? false} routes={crumbs} />
        )}
        <Typography weight="display" variant="h1" uppercase>
          {formSubmitted ? 'Online Order' : 'Track Your Order'}
        </Typography>
      </div>
      <div className="sui-col-span-12 sui-p-2">
        <ErrorBoundary
          name={formSubmitted ? 'Online Order' : 'Track Your Order'}
        >
          {/* 🚨 Not currently needed, update once persisted state is added back - <OnlineOrderSkeleton />} */}
          {(() => {
            if (formSubmitted) {
              return (
                <div className="sui-grid sui-grid-cols-12">
                  <div className="sui-col-span-12 xl:sui-col-span-9">
                    <Phase0BarcodeProvider
                      orderDetailsData={formData || undefined}
                      orderDetailsLoaded
                    >
                      <EditDeliveryInstructionsProvider>
                        <OnlineOrder
                          customerType={customerType}
                          formData={formData}
                        />
                      </EditDeliveryInstructionsProvider>
                    </Phase0BarcodeProvider>
                  </div>
                  <div className="sui-col-span-12 sui-self-center sui-justify-self-center sui-pt-4 xl:sui-px-4 xl:sui-col-span-3 xl:sui-justify-self-end">
                    <HelpDialogBox />
                  </div>
                </div>
              );
            }

            return (
              <TrackOrderForm
                setFormData={setFormData}
                setFormSubmitted={setFormSubmitted}
                orderId={orderNumberFromUrl}
                customerType={customerType}
              />
            );
          })()}
        </ErrorBoundary>
      </div>
    </div>
  );
};

export const GuestOrderTracking = ({
  customerType,
}: IGuestOrderTrackingProps) => (
  <CommonProvider>
    <OrderProvider>
      <GuestOrderTrackingContent customerType={customerType} />
    </OrderProvider>
  </CommonProvider>
);

GuestOrderTracking.displayName = 'Guest Order Tracking';
